import React, { useState } from 'react';
import { Badge, Popover, List, Tag, Button, Empty, Typography, Space, Tooltip, Switch } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined, SoundOutlined, AudioMutedOutlined, CompressOutlined, ExpandOutlined } from '@ant-design/icons';
import { useAlertNotifications } from '../../contexts/useAlertNotifications';
import { useEvent } from '../../hooks/useEvent';
import { useTheme } from '../../contexts/useTheme';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { ALERT_TYPE_CONFIG, SEVERITY_CONFIG, type EventAlert } from '../../api/alertsService';

const { Text } = Typography;

/**
 * Alert badge component for the top bar
 * Shows notification count and dropdown with active alerts
 */
export const AlertBadge: React.FC = () => {
  const { selectedEvent } = useEvent();
  const { theme } = useTheme();
  const {
    activeAlerts,
    unreadCount,
    dismissAlert,
    markAllAsSeen,
    popupAlertsMuted,
    setPopupAlertsMuted,
    compactAlerts,
    setCompactAlerts,
  } = useAlertNotifications();

  const [open, setOpen] = useState(false);

  // Don't show for legacy events or when no event selected
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;
  
  // Check if event is currently active
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  if (!isNonLegacyEvent) {
    return null;
  }

  const circuitTimeZone = selectedEvent?.circuit?.time_zone;

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (circuitTimeZone) {
      return date.toLocaleString('en-US', {
        timeZone: circuitTimeZone,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const handleDismiss = (e: React.MouseEvent, alert: EventAlert) => {
    e.stopPropagation();
    // Dismissing from the badge dropdown hides the alert entirely
    // (different from acknowledging a specific trigger in a toast)
    dismissAlert(alert.id);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && unreadCount > 0) {
      // Mark as seen when opening the popover
      markAllAsSeen();
    }
  };

  const getSeverityColor = (severity: string) => {
    return SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG]?.color || '#1890ff';
  };

  const secondaryColor = theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  const alertControls = (
    <Space size={12}>
      <Tooltip title={popupAlertsMuted ? 'Unmute popup alerts' : 'Mute popup alerts'}>
        <Space align="center" size={4} style={{ cursor: 'pointer' }}>
          {popupAlertsMuted ? (
            <AudioMutedOutlined style={{ fontSize: 14, color: secondaryColor }} />
          ) : (
            <SoundOutlined style={{ fontSize: 14, color: secondaryColor }} />
          )}
          <Switch
            size="small"
            checked={!popupAlertsMuted}
            onChange={(checked) => setPopupAlertsMuted(!checked)}
          />
        </Space>
      </Tooltip>
      <Tooltip title={compactAlerts ? 'Switch to full alerts' : 'Switch to compact alerts'}>
        <Space align="center" size={4} style={{ cursor: 'pointer' }}>
          {compactAlerts ? (
            <CompressOutlined style={{ fontSize: 14, color: secondaryColor }} />
          ) : (
            <ExpandOutlined style={{ fontSize: 14, color: secondaryColor }} />
          )}
          <Switch
            size="small"
            checked={compactAlerts}
            onChange={(checked) => setCompactAlerts(checked)}
          />
        </Space>
      </Tooltip>
    </Space>
  );

  const content = (
    <div style={{ width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 400, overflow: 'auto' }}>
      {activeAlerts.length === 0 ? (
        <>
          <div style={{ 
            padding: '8px 12px', 
            borderBottom: `1px solid ${theme === 'dark' ? '#303030' : '#f0f0f0'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Text strong>Alerts (0)</Text>
            {alertControls}
          </div>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
            description="No active alerts"
            style={{ padding: '20px 0' }}
          />
        </>
      ) : (
        <>
          <div style={{ 
            padding: '8px 12px', 
            borderBottom: `1px solid ${theme === 'dark' ? '#303030' : '#f0f0f0'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}>
            <Space>
              <Text strong>Alerts ({activeAlerts.length})</Text>
              {!isEventActive && <Tag color="default">Event ended</Tag>}
            </Space>
            {alertControls}
          </div>
          <List
            dataSource={activeAlerts.slice(0, 10)} // Show max 10 in dropdown
            renderItem={(alert) => {
              const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type];
              return (
                <List.Item
                  style={{ 
                    padding: '12px',
                    borderLeft: `3px solid ${getSeverityColor(alert.severity)}`,
                    marginBottom: 1,
                    background: theme === 'dark' ? '#1f1f1f' : '#fafafa',
                  }}
                  actions={isEventActive ? [
                    <Tooltip title={alert.requires_acknowledgment ? 'Acknowledge' : 'Dismiss'} key="dismiss">
                      <Button
                        type="text"
                        size="small"
                        icon={alert.requires_acknowledgment ? <CheckOutlined /> : <CloseOutlined />}
                        onClick={(e) => handleDismiss(e, alert)}
                      />
                    </Tooltip>
                  ] : undefined}
                >
                  <List.Item.Meta
                    title={
                      <Space size={4}>
                        <Tag color={SEVERITY_CONFIG[alert.severity]?.color} style={{ margin: 0 }}>
                          {alert.severity.toUpperCase()}
                        </Tag>
                        <Text strong style={{ fontSize: 13 }}>{alert.title}</Text>
                      </Space>
                    }
                    description={
                      <div>
                        <Text 
                          type="secondary" 
                          style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                          ellipsis={{ tooltip: alert.message }}
                        >
                          {alert.message}
                        </Text>
                        <Space size={8} style={{ fontSize: 11 }}>
                          <span>{typeConfig?.icon} {typeConfig?.label}</span>
                          <span style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
                            {formatTime(alert.start_time)}
                          </span>
                          {alert.requires_acknowledgment && (
                            <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>ACK REQUIRED</Tag>
                          )}
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
          {activeAlerts.length > 10 && (
            <div style={{ 
              padding: '8px 12px', 
              textAlign: 'center',
              borderTop: `1px solid ${theme === 'dark' ? '#303030' : '#f0f0f0'}`,
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                +{activeAlerts.length - 10} more alerts
              </Text>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Emphasize bell when alerts are active and popups are not muted
  const alertsActiveAndUnmuted = activeAlerts.length > 0 && !popupAlertsMuted;
  const emphasisColor = alertsActiveAndUnmuted
    ? (activeAlerts.some(a => a.severity === 'critical')
        ? SEVERITY_CONFIG.critical.color
        : activeAlerts.some(a => a.severity === 'warning')
          ? SEVERITY_CONFIG.warning.color
          : SEVERITY_CONFIG.info.color)
    : undefined;

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      arrow={false}
    >
      <Badge 
        count={unreadCount} 
        size="small"
        offset={[-2, 2]}
        style={{ 
          backgroundColor: activeAlerts.some(a => a.severity === 'critical') 
            ? SEVERITY_CONFIG.critical.color 
            : activeAlerts.some(a => a.severity === 'warning')
              ? SEVERITY_CONFIG.warning.color
              : SEVERITY_CONFIG.info.color
        }}
      >
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: emphasisColor,
            ...(alertsActiveAndUnmuted && emphasisColor && {
              boxShadow: `0 0 0 2px ${emphasisColor}40`,
              borderRadius: 4,
            }),
          }}
        />
      </Badge>
    </Popover>
  );
};

export default AlertBadge;

